/**
 * Centralised search parameter schemas shared across Blockdaemon widget hosts.
 */

import { z } from "zod";

const COLOR_SCHEMES = ["light", "dark"] as const;
const WALLET_MODES = ["internal", "external"] as const;
const CURRENCIES = ["USD", "KRW", "JPY"] as const;
const LOCALES = ["en-US", "ko-KR", "ja-JP"] as const;

const WIDGET_DIMENSION_PRESETS = ["mobile", "tablet", "desktop"] as const;

function parseWidgetDimensionParam(
  val: string | number,
  bounds: { min: number; max: number },
): (typeof WIDGET_DIMENSION_PRESETS)[number] | number | undefined {
  if (typeof val === "number") {
    return val >= bounds.min && val <= bounds.max ? val : undefined;
  }
  if ((WIDGET_DIMENSION_PRESETS as readonly string[]).includes(val)) {
    return val as (typeof WIDGET_DIMENSION_PRESETS)[number];
  }
  const num = Number.parseInt(val, 10);
  if (!Number.isNaN(num) && num >= bounds.min && num <= bounds.max) {
    return num;
  }
  return undefined;
}

function createLenientEnumParam<const T extends readonly [string, ...string[]]>(values: T) {
  return z
    .string()
    .optional()
    .transform((value) => {
      if (!value) {
        return undefined;
      }

      return values.includes(value as (typeof values)[number])
        ? (value as (typeof values)[number])
        : undefined;
    });
}

function createOptionalTruthyBooleanParam() {
  return z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((val) => {
      if (typeof val === "string") {
        return val === "true" || val === "1";
      }
      return val;
    });
}

export const strictRootSearchSchema = z.object({
  embed: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((val) => {
      if (typeof val === "string") {
        return val === "true";
      }
      return val;
    }),
  colorScheme: z.enum(COLOR_SCHEMES).optional(),
  width: z
    .union([z.string(), z.number()])
    .transform((val) => parseWidgetDimensionParam(val, { min: 375, max: 1366 }))
    .optional(),
  height: z
    .union([z.string(), z.number()])
    .transform((val) => parseWidgetDimensionParam(val, { min: 480, max: 1200 }))
    .optional(),
  walletMode: z.enum(WALLET_MODES).optional(),
  currency: z.enum(CURRENCIES).optional(),
  locale: z.enum(LOCALES).optional(),
  bdDevTools: createOptionalTruthyBooleanParam(),
});

export const rootSearchSchema = z.object({
  embed: strictRootSearchSchema.shape.embed,
  colorScheme: createLenientEnumParam(COLOR_SCHEMES),
  width: strictRootSearchSchema.shape.width,
  height: strictRootSearchSchema.shape.height,
  walletMode: createLenientEnumParam(WALLET_MODES),
  currency: createLenientEnumParam(CURRENCIES),
  locale: createLenientEnumParam(LOCALES),
  bdDevTools: strictRootSearchSchema.shape.bdDevTools,
});

export type RootSearch = z.infer<typeof rootSearchSchema>;

export const positionsSearchSchema = z.object({
  protocolKey: z.string().optional(),
});

export const stablecoinsSearchSchema = z.object({
  protocolKey: z.string().optional(),
});

export const selectProtocolSearchSchema = z.object({});

export const protocolTokensSearchSchema = z.object({
  vaultId: z.string().optional(),
  protocolId: z.string().optional(),
});

export const poolDetailSearchSchema = z.object({
  mode: z.enum(["create", "supply", "withdraw"]).optional(),
});

export const swapSearchSchema = z.object({
  fromChainId: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => (val !== undefined ? String(val) : undefined)),
  fromTokenAddress: z
    .string()
    .optional()
    .transform((val) => val?.toLowerCase()),
  toChainId: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => (val !== undefined ? String(val) : undefined)),
  toTokenAddress: z
    .string()
    .optional()
    .transform((val) => val?.toLowerCase()),
});

/**
 * Embed-only URL overrides read directly from the query string (see embed-detection).
 * Not part of TanStack Router search schemas or RETAINED_SEARCH_PARAMS.
 */
const embedOverrideSearchSchema = z.object({
  batchMode: z.string().optional(),
  atomicRequired: createOptionalTruthyBooleanParam(),
  batcherContract: z.string().optional(),
  enableFee: createOptionalTruthyBooleanParam(),
  /** Session-only testing overrides (tokenization); not retained on navigation. */
  partyId: z.string().optional(),
  transactionId: z.string().optional(),
});

function getSchemaKeys<T extends z.ZodRawShape>(schema: z.ZodObject<T>): string[] {
  return Object.keys(schema.shape);
}

/**
 * Search params that are forwarded on every navigation so they survive
 * route transitions (e.g. showcase embed preview, colour scheme, sizing).
 * bdDevTools is intentionally excluded — it is a dev-only escape-hatch.
 */
export const RETAINED_SEARCH_PARAMS = [
  "embed",
  "colorScheme",
  "width",
  "height",
  "walletMode",
  "currency",
  "locale",
] as const satisfies ReadonlyArray<keyof z.infer<typeof rootSearchSchema>>;

export const ALLOWED_QUERY_PARAMETERS: readonly string[] = [
  ...new Set([
    ...getSchemaKeys(strictRootSearchSchema),
    ...getSchemaKeys(embedOverrideSearchSchema),
    ...getSchemaKeys(positionsSearchSchema),
    ...getSchemaKeys(stablecoinsSearchSchema),
    ...getSchemaKeys(selectProtocolSearchSchema),
    ...getSchemaKeys(protocolTokensSearchSchema),
    ...getSchemaKeys(poolDetailSearchSchema),
    ...getSchemaKeys(swapSearchSchema),
  ]),
];
